<template>
  <div class="bc-page">
    <div class="bc-phone">

      <!-- STEP 1: Birth Info Form -->
      <div v-if="step === 'form'" class="bc-form-step">
        <h2>Your Birth Details</h2>
        <form @submit.prevent="submitBirthInfo" class="birth-form">
          <div class="form-group">
            <label>Name</label>
            <input v-model="form.name" type="text" placeholder="Your name" required>
          </div>
          <div class="form-row">
            <div class="form-group">
              <label>Birth Date</label>
              <input v-model="form.birthDate" type="date" min="1920-01-01" max="2025-12-31" required>
            </div>
            <div class="form-group">
              <label>Birth Time</label>
              <input v-model="form.birthTime" type="time" required>
            </div>
          </div>
          <div class="form-group">
            <label>Birth Place</label>
            <location-search @location-selected="onLocationSelected" />
          </div>
          <div class="form-group">
            <label>Gender</label>
            <div class="gender-options">
              <label v-for="g in genders" :key="g.value" class="gender-option" :class="{ active: form.gender === g.value }">
                <input type="radio" v-model="form.gender" :value="g.value" class="sr-only">
                <span>{{ g.label }}</span>
              </label>
            </div>
          </div>
          <button type="submit" class="primary-btn" :disabled="!isFormValid">Continue</button>
        </form>
      </div>

      <!-- STEP 2: Loading -->
      <div v-else-if="step === 'loading'" class="bc-loading-step">
        <div class="loader-ring">
          <svg viewBox="0 0 100 100">
            <circle class="ring-bg" cx="50" cy="50" r="42" />
            <circle class="ring-fg" cx="50" cy="50" r="42" :stroke-dasharray="progress * 2.64 + ', 264'" />
          </svg>
          <span class="ring-pct">{{ Math.round(progress) }}%</span>
        </div>
        <p class="loading-text">{{ loadingMessage }}</p>
      </div>

      <!-- STEP 3: Chart -->
      <template v-else-if="step === 'chart'">
        <!-- Error -->
        <div v-if="error" class="bc-error">
          <p>{{ error }}</p>
          <button @click="retryGenerate" class="bc-retry-btn">Retry</button>
        </div>

        <template v-else>
          <!-- Header -->
          <div class="bc-header">
            <button class="bc-back-btn" @click="step = 'form'">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M19 12H5M12 19l-7-7 7-7"/></svg>
            </button>
            <h1 class="bc-title">{{ userName }} Birth Chart</h1>
            <p class="bc-subtitle">Your birth chart reveals different parts of who you are.</p>
          </div>

          <!-- Circle / Table Toggle -->
          <div class="bc-toggle">
            <button :class="['bc-toggle-btn', { active: viewMode === 'circle' }]" @click="viewMode = 'circle'">Circle</button>
            <button :class="['bc-toggle-btn', { active: viewMode === 'table' }]" @click="viewMode = 'table'">Table</button>
          </div>

          <!-- Circle View -->
          <div v-if="viewMode === 'circle'" class="bc-chart-container">
            <svg viewBox="0 0 340 340" class="bc-chart-svg">
              <defs>
                <radialGradient id="chartGlow" cx="50%" cy="50%" r="50%">
                  <stop offset="0%" stop-color="rgba(108,92,231,0.08)" />
                  <stop offset="100%" stop-color="transparent" />
                </radialGradient>
              </defs>
              <circle cx="170" cy="170" r="168" fill="url(#chartGlow)" />
              <circle cx="170" cy="170" r="155" fill="none" stroke="rgba(255,255,255,0.08)" stroke-width="1" />
              <circle cx="170" cy="170" r="120" fill="none" stroke="rgba(255,255,255,0.06)" stroke-width="1" />
              <circle cx="170" cy="170" r="85" fill="none" stroke="rgba(255,255,255,0.06)" stroke-width="1" />
              <circle cx="170" cy="170" r="30" fill="none" stroke="rgba(255,255,255,0.04)" stroke-width="1" />
              <line v-for="i in 12" :key="'line-'+i"
                :x1="170 + 85 * Math.cos((i * 30 - 90) * Math.PI / 180)"
                :y1="170 + 85 * Math.sin((i * 30 - 90) * Math.PI / 180)"
                :x2="170 + 155 * Math.cos((i * 30 - 90) * Math.PI / 180)"
                :y2="170 + 155 * Math.sin((i * 30 - 90) * Math.PI / 180)"
                stroke="rgba(255,255,255,0.05)" stroke-width="0.5"
              />
              <text v-for="(sign, idx) in zodiacSigns" :key="'sign-'+idx"
                :x="170 + 138 * Math.cos(((idx * 30) + 15 - 90) * Math.PI / 180)"
                :y="170 + 138 * Math.sin(((idx * 30) + 15 - 90) * Math.PI / 180)"
                text-anchor="middle" dominant-baseline="central"
                class="bc-zodiac-label"
              >{{ sign.symbol }}</text>
              <g v-for="(planet, idx) in displayPlanets" :key="'planet-'+idx">
                <circle
                  :cx="170 + 102 * Math.cos((planet.angle - 90) * Math.PI / 180)"
                  :cy="170 + 102 * Math.sin((planet.angle - 90) * Math.PI / 180)"
                  r="10" :fill="planet.color" fill-opacity="0.2"
                  :stroke="planet.color" stroke-width="0.5"
                />
                <text
                  :x="170 + 102 * Math.cos((planet.angle - 90) * Math.PI / 180)"
                  :y="170 + 102 * Math.sin((planet.angle - 90) * Math.PI / 180)"
                  text-anchor="middle" dominant-baseline="central"
                  class="bc-planet-label"
                >{{ planet.symbol }}</text>
              </g>
              <line v-for="(aspect, idx) in displayAspects" :key="'aspect-'+idx"
                :x1="170 + 60 * Math.cos((aspect.fromAngle - 90) * Math.PI / 180)"
                :y1="170 + 60 * Math.sin((aspect.fromAngle - 90) * Math.PI / 180)"
                :x2="170 + 60 * Math.cos((aspect.toAngle - 90) * Math.PI / 180)"
                :y2="170 + 60 * Math.sin((aspect.toAngle - 90) * Math.PI / 180)"
                :stroke="aspect.color" stroke-width="0.5" opacity="0.4"
              />
            </svg>
          </div>

          <!-- Table View -->
          <div v-else class="bc-table-container">
            <div class="bc-table-header">
              <span>Sign</span>
              <span>Placement</span>
            </div>
            <div v-for="(row, idx) in tableData" :key="'row-'+idx" class="bc-table-row">
              <div class="bc-table-sign">
                <span class="bc-table-sign-icon">{{ row.icon }}</span>
                <span>{{ row.sign }}</span>
              </div>
              <div class="bc-table-placements">
                <span v-for="(p, pi) in row.placements" :key="pi" class="bc-table-planet-tag">
                  <span class="bc-table-planet-icon">{{ p.icon }}</span>
                  {{ p.name }}
                </span>
              </div>
            </div>
          </div>

          <!-- Daily Insights label -->
          <div class="bc-insights-header">
            <span class="bc-insights-label">Daily Insights</span>
          </div>

          <!-- Insight Cards -->
          <div class="bc-insights">
            <div v-for="(insight, idx) in insights" :key="idx"
                 class="bc-card"
                 :class="{ 'bc-card-expanded': insight.expanded }"
                 @click="toggleInsight(idx)"
            >
              <div class="bc-card-header">
                <div class="bc-card-icon">{{ insightIcons[insight.title] || '✨' }}</div>
                <div class="bc-card-info">
                  <div class="bc-card-title-row">
                    <h3>{{ insight.title }}</h3>
                  </div>
                  <p class="bc-card-desc">{{ insight.description }}</p>
                </div>
                <div class="bc-card-arrow">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" :style="{ transform: insight.expanded ? 'rotate(180deg)' : '' }">
                    <path d="M6 9l6 6 6-6"/>
                  </svg>
                </div>
              </div>

              <!-- Expanded Content -->
              <div v-if="insight.expanded" class="bc-card-body" @click.stop>
                <div v-if="insight.chartData" class="bc-inner-toggle">
                  <button :class="['bc-inner-toggle-btn', { active: insight.innerTab === 'foryou' }]" @click="insight.innerTab = 'foryou'">For You</button>
                  <button :class="['bc-inner-toggle-btn', { active: insight.innerTab === 'chart' }]" @click="insight.innerTab = 'chart'">Chart</button>
                </div>

                <div v-if="!insight.chartData || insight.innerTab === 'foryou'" class="bc-card-content">
                  <p class="bc-card-paragraph">{{ insight.forYouText }}</p>
                </div>

                <div v-if="insight.chartData && insight.innerTab === 'chart'" class="bc-card-content">
                  <div class="bc-placements">
                    <h4 class="bc-placements-title">Your Placements</h4>
                    <p class="bc-placements-subtitle">The planetary positions behind your core traits</p>
                    <div v-for="(placement, pi) in insight.chartData.placements" :key="pi" class="bc-placement-item">
                      <div class="bc-placement-header">
                        <span class="bc-placement-name">{{ placement.label }}</span>
                        <span class="bc-placement-sign-icon">{{ placement.signIcon }}</span>
                      </div>
                      <p class="bc-placement-desc">{{ placement.description }}</p>
                    </div>
                    <div v-if="insight.chartData.summary" class="bc-placement-summary">
                      <div class="bc-placement-divider"></div>
                      <h4 class="bc-placements-title">What this means for you</h4>
                      <p class="bc-placement-desc">{{ insight.chartData.summary }}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </template>
      </template>

    </div>
  </div>
</template>

<script>
import { ref, computed } from 'vue'
import { signInAnonymously } from 'firebase/auth'
import { doc, setDoc, getDoc, serverTimestamp } from 'firebase/firestore'
import { httpsCallable } from 'firebase/functions'
import { initFirebase } from '../firebase'
import LocationSearch from './LocationSearch.vue'

const { db, auth, functions } = initFirebase()

export default {
  name: 'BirthChartPreview',
  components: { LocationSearch },
  setup() {
    const step = ref('form') // 'form' | 'loading' | 'chart'
    const viewMode = ref('circle')
    const error = ref(null)
    const chartData = ref(null)
    const userName = ref('')
    const insights = ref([])
    const progress = ref(0)
    const loadingMessage = ref('Aligning your celestial blueprint...')
    let progressTimer = null

    // Form
    const form = ref({ name: '', birthDate: '', birthTime: '', gender: '', location: null })
    const genders = [
      { value: 'female', label: 'Female' },
      { value: 'male', label: 'Male' },
      { value: 'non-binary', label: 'Non-binary' },
      { value: 'prefer_not_to_say', label: 'Prefer not to say' }
    ]
    const isFormValid = computed(() => form.value.name && form.value.birthDate && form.value.birthTime && form.value.location && form.value.gender)
    const onLocationSelected = (loc) => { form.value.location = loc }

    // Frontend mappings
    const zodiacSigns = [
      { name: 'Aries', symbol: '♈' }, { name: 'Taurus', symbol: '♉' },
      { name: 'Gemini', symbol: '♊' }, { name: 'Cancer', symbol: '♋' },
      { name: 'Leo', symbol: '♌' }, { name: 'Virgo', symbol: '♍' },
      { name: 'Libra', symbol: '♎' }, { name: 'Scorpio', symbol: '♏' },
      { name: 'Sagittarius', symbol: '♐' }, { name: 'Capricorn', symbol: '♑' },
      { name: 'Aquarius', symbol: '♒' }, { name: 'Pisces', symbol: '♓' }
    ]

    const signSymbolMap = {}
    zodiacSigns.forEach(s => { signSymbolMap[s.name] = s.symbol })

    const planetMeta = {
      Sun: { symbol: '☉', color: '#fbbf24' },
      Moon: { symbol: '☽', color: '#c4b5fd' },
      Mercury: { symbol: '☿', color: '#6ee7b7' },
      Venus: { symbol: '♀', color: '#f9a8d4' },
      Mars: { symbol: '♂', color: '#fca5a5' },
      Jupiter: { symbol: '♃', color: '#93c5fd' },
      Saturn: { symbol: '♄', color: '#d4d4d8' },
      Uranus: { symbol: '♅', color: '#67e8f9' },
      Neptune: { symbol: '♆', color: '#a78bfa' },
      Pluto: { symbol: '♇', color: '#fda4af' },
      Ascendant: { symbol: 'AC', color: '#fdba74' },
      Node: { symbol: '☊', color: '#a3e635' },
      Chiron: { symbol: '⚷', color: '#fb923c' },
      'Part of Fortune': { symbol: '⊕', color: '#e879f9' }
    }

    const insightIcons = {
      'Snapshot': '✨', 'Core Personality': '⭐', 'Emotional Patterns': '💧',
      'Communication Style': '💬', 'Love & Relationship Patterns': '❤️',
      'Self Worth & Values': '💎', 'Drive Ambition Action': '☄️',
      'Strengths': '💪', 'Weaknesses': '🪞', 'Shadow Work Themes': '🌑',
      'Career & Life Purpose': '🎯', 'Life Challenges': '⚡',
      'Repeating Patterns': '🔄', 'How Others See You': '👁️',
      'Compatibility Tendencies': '🪐'
    }

    const displayPlanets = computed(() => {
      if (!chartData.value?.planets) return []
      return chartData.value.planets.map(p => {
        const meta = planetMeta[p.name] || { symbol: p.name.charAt(0), color: '#fff' }
        return { ...p, symbol: meta.symbol, color: meta.color }
      })
    })

    const displayAspects = computed(() => {
      if (!chartData.value?.aspects) return []
      return chartData.value.aspects.map(a => ({
        fromAngle: a.fromAngle,
        toAngle: a.toAngle,
        color: (planetMeta[a.from] || { color: '#fff' }).color
      }))
    })

    const tableData = computed(() => {
      if (!chartData.value?.planets) return []
      const groups = {}
      chartData.value.planets.forEach(p => {
        if (!groups[p.sign]) groups[p.sign] = []
        const meta = planetMeta[p.name] || { symbol: p.name.charAt(0) }
        groups[p.sign].push({ name: p.name, icon: meta.symbol })
      })
      return Object.entries(groups).map(([sign, placements]) => ({
        sign, icon: signSymbolMap[sign] || '', placements
      }))
    })

    const toggleInsight = (idx) => { insights.value[idx].expanded = !insights.value[idx].expanded }

    // Progress animation
    const startProgress = () => {
      progress.value = 0
      progressTimer = setInterval(() => {
        if (progress.value < 40) progress.value += 2
        else if (progress.value < 75) progress.value += 0.5
        else if (progress.value < 95) progress.value += 0.15
      }, 100)
    }
    const finishProgress = () => {
      return new Promise(resolve => {
        clearInterval(progressTimer)
        const finish = setInterval(() => {
          progress.value += 3
          if (progress.value >= 100) {
            progress.value = 100
            clearInterval(finish)
            setTimeout(resolve, 300)
          }
        }, 30)
      })
    }

    // Map Firestore astrology data to chart display format
    const applyAstrologyData = (astrology) => {
      if (!astrology) return
      const planets = []
      if (Array.isArray(astrology.planets)) {
        astrology.planets.forEach(p => {
          planets.push({
            name: p.planet,
            sign: p.zodiac,
            degree: p.degree,
            angle: p.fullDegree
          })
        })
      }
      // Add Ascendant
      if (astrology.ascendant) {
        const ascDegree = typeof astrology.ascendant === 'number' ? astrology.ascendant : parseFloat(astrology.ascendant)
        const signIndex = Math.floor(ascDegree / 30)
        const signs = ['Aries','Taurus','Gemini','Cancer','Leo','Virgo','Libra','Scorpio','Sagittarius','Capricorn','Aquarius','Pisces']
        planets.push({
          name: 'Ascendant',
          sign: signs[signIndex] || 'Aries',
          degree: ascDegree % 30,
          angle: ascDegree
        })
      }
      const aspects = []
      if (Array.isArray(astrology.aspects)) {
        astrology.aspects.forEach(a => {
          const fromPlanet = planets.find(p => p.name === a.planet1)
          const toPlanet = planets.find(p => p.name === a.planet2)
          if (fromPlanet && toPlanet) {
            aspects.push({
              from: a.planet1,
              to: a.planet2,
              type: a.aspect,
              fromAngle: fromPlanet.angle,
              toAngle: toPlanet.angle
            })
          }
        })
      }
      chartData.value = { planets, aspects }
    }

    // Apply insights from generateBirthChartV2 response
    const applyInsights = (rawData) => {
      let data = rawData
      if (data?.data?.insights) data = data.data
      if (data?.insights && Array.isArray(data.insights)) {
        insights.value = data.insights.map(ins => ({
          title: ins.title,
          description: ins.description,
          forYouText: ins.forYouText,
          chartData: ins.chartData || null,
          expanded: false,
          innerTab: 'foryou'
        }))
      }
    }

    const submitBirthInfo = async () => {
      if (!isFormValid.value) return
      step.value = 'loading'
      loadingMessage.value = 'Aligning your celestial blueprint...'
      startProgress()
      error.value = null

      try {
        // Create anonymous user
        if (auth.currentUser) await auth.signOut()
        const uid = await new Promise((resolve) => {
          const timeout = setTimeout(async () => {
            const cred = await signInAnonymously(auth)
            resolve(cred.user.uid)
          }, 3000)
          const unsub = auth.onAuthStateChanged((user) => {
            if (user) { unsub(); clearTimeout(timeout); resolve(user.uid) }
          })
        })

        userName.value = form.value.name
        const [year, month, date] = form.value.birthDate.split('-').map(Number)
        const [hours, minutes] = form.value.birthTime.split(':').map(Number)

        // Save user to Firestore
        const userData = {
          uid,
          createdAt: serverTimestamp(),
          properties: {
            name: form.value.name,
            date, month, year, hours, minutes, seconds: 0,
            address: form.value.location.name,
            latitude: form.value.location.lat,
            longitude: form.value.location.lng,
            guessCountry: form.value.location.name?.split(',').pop()?.trim() || '',
            gender: form.value.gender,
            languageStyle: 'balanced',
            updateSystemPrompt: true,
            isWeb: true
          }
        }
        await setDoc(doc(db, 'users', uid), userData, { merge: true })

        // Verify
        const verify = await getDoc(doc(db, 'users', uid))
        if (!verify.exists()) throw new Error('User document not found after save')

        // Call summary — generates natal chart in users/{uid}.astrology
        loadingMessage.value = 'Calculating your natal chart...'
        const summaryFn = httpsCallable(functions, 'summary')
        await summaryFn({})

        // Read astrology data from Firestore
        loadingMessage.value = 'Reading your natal chart...'
        const userDoc = await getDoc(doc(db, 'users', uid))
        const astrology = userDoc.data()?.astrology
        applyAstrologyData(astrology)

        // Call generateBirthChartV2 — generates AI insights only
        loadingMessage.value = 'Generating personalized insights...'
        const generateFn = httpsCallable(functions, 'generateBirthChartV2')
        const result = await generateFn({})
        applyInsights(result.data)

        await finishProgress()
        step.value = 'chart'
      } catch (e) {
        clearInterval(progressTimer)
        console.error('[BirthChart] Error:', e)
        error.value = e.message || 'Failed to generate birth chart'
        step.value = 'chart'
      }
    }

    const retryGenerate = async () => {
      step.value = 'loading'
      loadingMessage.value = 'Retrying...'
      startProgress()
      error.value = null
      try {
        const generateFn = httpsCallable(functions, 'generateBirthChartV2')
        const result = await generateFn({})
        applyInsights(result.data)
        await finishProgress()
        step.value = 'chart'
      } catch (e) {
        clearInterval(progressTimer)
        error.value = e.message || 'Failed to generate birth chart'
        step.value = 'chart'
      }
    }

    return {
      step, viewMode, error, chartData, userName, progress, loadingMessage,
      form, genders, isFormValid, onLocationSelected, submitBirthInfo, retryGenerate,
      zodiacSigns, displayPlanets, displayAspects, tableData,
      insights, insightIcons, toggleInsight
    }
  }
}
</script>

<style scoped>
.bc-page {
  position: fixed;
  inset: 0;
  background: #08071a;
  overflow-x: hidden;
  overflow-y: auto;
  overscroll-behavior-x: none;
  touch-action: pan-y;
  z-index: 100;
  -webkit-overflow-scrolling: touch;
}

.bc-phone {
  width: 100%;
  max-width: 402px;
  min-height: 100vh;
  margin: 0 auto;
  overflow: hidden;
  background: linear-gradient(180deg, #0e0c1f 0%, #131127 50%, #0e0c1f 100%);
  display: flex;
  flex-direction: column;
}

/* ===== FORM STEP ===== */
.bc-form-step { padding: 40px 20px 20px; text-align: center; }
.bc-form-step h2 { font-size: 24px; font-weight: 700; color: #fff; margin-bottom: 28px; }
.birth-form { display: flex; flex-direction: column; gap: 20px; text-align: left; }
.form-group { display: flex; flex-direction: column; gap: 8px; }
.form-group > label { font-size: 12px; color: #DBC087; text-transform: uppercase; letter-spacing: 0.5px; font-weight: 600; }
.form-row { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; }
.form-group input[type="text"],
.form-group input[type="date"],
.form-group input[type="time"] {
  height: 48px; box-sizing: border-box;
  padding: 0 16px; background: rgba(255,255,255,0.05); border: 1px solid rgba(255,255,255,0.15);
  border-radius: 12px; color: #fff; font-size: 15px; color-scheme: dark; font-family: inherit;
}
.form-group input::placeholder { color: rgba(255,255,255,0.3); }
.form-group input:focus { outline: none; border-color: rgba(255,255,255,0.3); }
.gender-options { display: flex; flex-direction: column; gap: 12px; width: 100%; }
.gender-option { padding: 14px 16px; background: rgba(255,255,255,0.05); border: 1px solid rgba(255,255,255,0.12); border-radius: 12px; color: #fff; font-size: 15px; font-weight: 500; cursor: pointer; transition: all 0.2s; text-align: left; }
.gender-option:hover { background: rgba(255,255,255,0.08); }
.gender-option.active { background: rgba(219,192,135,0.1); border-color: #DBC087; }
.sr-only { position: absolute; width: 1px; height: 1px; padding: 0; margin: -1px; overflow: hidden; clip: rect(0,0,0,0); border: 0; }
.primary-btn { padding: 16px 24px; background: #DBC087; border: none; border-radius: 12px; color: #0e0c1f; font-size: 16px; font-weight: 600; cursor: pointer; transition: all 0.2s; margin-top: 4px; font-family: inherit; }
.primary-btn:disabled { opacity: 0.4; cursor: not-allowed; }
.primary-btn:hover:not(:disabled) { box-shadow: 0 6px 20px rgba(219,192,135,0.35); }

/* ===== LOADING STEP ===== */
.bc-loading-step { display: flex; flex-direction: column; align-items: center; justify-content: center; flex: 1; gap: 24px; }
.loader-ring { position: relative; width: 120px; height: 120px; }
.loader-ring svg { width: 100%; height: 100%; transform: rotate(-90deg); }
.ring-bg { fill: none; stroke: rgba(255,255,255,0.06); stroke-width: 4; }
.ring-fg { fill: none; stroke: #DBC087; stroke-width: 4; stroke-linecap: round; transition: stroke-dasharray 0.3s ease; }
.ring-pct { position: absolute; inset: 0; display: flex; align-items: center; justify-content: center; font-size: 24px; font-weight: 800; color: #fff; }
.loading-text { font-size: 16px; color: rgba(255,255,255,0.6); }

/* ===== ERROR ===== */
.bc-error { display: flex; flex-direction: column; align-items: center; justify-content: center; flex: 1; gap: 16px; color: rgba(255,255,255,0.5); font-size: 14px; padding: 20px; text-align: center; }
.bc-retry-btn { padding: 10px 24px; border: 1px solid rgba(219,192,135,0.3); border-radius: 12px; background: rgba(219,192,135,0.1); color: #DBC087; font-size: 14px; cursor: pointer; }

/* ===== CHART STEP ===== */
.bc-header { padding: 20px 18px 0; }
.bc-back-btn { background: none; border: none; color: rgba(255,255,255,0.5); cursor: pointer; padding: 4px; margin-bottom: 12px; }
.bc-title { font-size: 24px; font-weight: 700; color: #fff; margin-bottom: 6px; text-align: center; }
.bc-subtitle { font-size: 14px; color: rgba(255,255,255,0.45); line-height: 1.4; text-align: center; }

/* Top Toggle */
.bc-toggle { display: flex; gap: 4px; margin: 20px auto 0; background: rgba(255,255,255,0.06); border-radius: 20px; padding: 4px; width: fit-content; }
.bc-toggle-btn { padding: 8px 20px; border: none; border-radius: 16px; font-size: 13px; font-weight: 600; cursor: pointer; transition: all 0.2s; background: transparent; color: rgba(255,255,255,0.4); }
.bc-toggle-btn.active { background: rgba(108,92,231,0.3); color: #fff; }

/* Chart */
.bc-chart-container { padding: 10px 18px; display: flex; justify-content: center; }
.bc-chart-svg { width: 100%; max-width: 320px; }
.bc-zodiac-label { font-size: 11px; fill: rgba(255,255,255,0.35); }
.bc-planet-label { font-size: 8px; fill: #fff; font-weight: 600; }

/* Table */
.bc-table-container { margin: 16px 18px; }
.bc-table-header { display: flex; justify-content: space-between; padding: 10px 14px; font-size: 14px; font-weight: 700; color: rgba(255,255,255,0.8); }
.bc-table-row { display: flex; justify-content: space-between; align-items: center; padding: 14px; margin-bottom: 8px; background: rgba(255,255,255,0.03); border-radius: 14px; border: 1px solid rgba(255,255,255,0.05); }
.bc-table-sign { display: flex; align-items: center; gap: 10px; font-size: 14px; color: rgba(255,255,255,0.8); font-weight: 500; }
.bc-table-sign-icon { font-size: 20px; color: #DBC087; }
.bc-table-placements { display: flex; gap: 6px; flex-wrap: wrap; justify-content: flex-end; }
.bc-table-planet-tag { font-size: 13px; padding: 4px 12px; border-radius: 12px; background: rgba(108,92,231,0.1); color: rgba(255,255,255,0.65); display: flex; align-items: center; gap: 5px; }
.bc-table-planet-icon { color: #DBC087; font-size: 14px; }

/* Daily Insights Header */
.bc-insights-header { display: flex; align-items: center; padding: 20px 18px 10px; }
.bc-insights-label { font-size: 14px; font-weight: 600; color: rgba(255,255,255,0.6); }

/* Insight Cards */
.bc-insights { padding: 0 18px 100px; display: flex; flex-direction: column; gap: 10px; }
.bc-card {
  background: rgba(255,255,255,0.03);
  border: 1px solid rgba(255,255,255,0.06);
  border-radius: 16px;
  padding: 18px;
  cursor: pointer;
  transition: all 0.2s;
}
.bc-card:hover { background: rgba(255,255,255,0.05); }
.bc-card-header { display: flex; align-items: flex-start; gap: 12px; }
.bc-card-icon { width: 48px; height: 48px; border-radius: 14px; background: rgba(219,192,135,0.08); border: 1px solid rgba(219,192,135,0.12); display: flex; align-items: center; justify-content: center; font-size: 22px; flex-shrink: 0; }
.bc-card-info { flex: 1; min-width: 0; }
.bc-card-title-row { display: flex; align-items: center; gap: 8px; margin-bottom: 2px; }
.bc-card-title-row h3 { font-size: 15px; font-weight: 700; color: #fff; margin: 0; }
.bc-card-desc { font-size: 12px; color: rgba(255,255,255,0.35); margin: 0; line-height: 1.4; }
.bc-card-arrow { flex-shrink: 0; color: rgba(255,255,255,0.25); padding-top: 4px; }
.bc-card-arrow svg { transition: transform 0.2s; }

/* Expanded Card Body */
.bc-card-body { margin-top: 16px; cursor: default; }
.bc-inner-toggle { display: flex; gap: 4px; background: rgba(255,255,255,0.06); border-radius: 14px; padding: 4px; width: fit-content; margin-bottom: 14px; }
.bc-inner-toggle-btn { padding: 6px 16px; border: none; border-radius: 10px; font-size: 12px; font-weight: 600; cursor: pointer; transition: all 0.2s; background: transparent; color: rgba(255,255,255,0.4); }
.bc-inner-toggle-btn.active { background: rgba(219,192,135,0.15); color: #DBC087; }
.bc-card-content { }
.bc-card-paragraph { font-size: 13px; color: rgba(255,255,255,0.6); line-height: 1.6; margin: 0; }
.bc-placements { }
.bc-placements-title { font-size: 15px; font-weight: 700; color: #fff; margin: 0 0 4px; }
.bc-placements-subtitle { font-size: 12px; color: rgba(255,255,255,0.35); margin: 0 0 16px; }
.bc-placement-item { margin-bottom: 16px; }
.bc-placement-header { display: flex; align-items: center; gap: 6px; margin-bottom: 6px; }
.bc-placement-name { font-size: 14px; font-weight: 600; color: #fff; }
.bc-placement-sign-icon { font-size: 16px; color: #DBC087; }
.bc-placement-desc { font-size: 13px; color: rgba(255,255,255,0.55); line-height: 1.5; margin: 0; }
.bc-placement-summary { margin-top: 8px; }
.bc-placement-divider { height: 1px; background: rgba(255,255,255,0.06); margin-bottom: 14px; }

.bc-page::-webkit-scrollbar { width: 0; }
</style>
